import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './HoursImport.css';
import api from '../services/api';

const HoursImport = ({ user }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [csvData, setCsvData] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadUsers();
    loadProjects();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleImport = async () => {
    if (!csvData.trim()) {
      alert(t('hoursImport.enterDataAlert'));
      return;
    }

    const lines = csvData.trim().split('\n').filter(line => line.trim()).length;
    const userName = selectedUser
      ? users.find(u => u.id === parseInt(selectedUser))?.full_name || 'N/A'
      : 'automaticky podľa import listu';
    const projectName = selectedProject ? projects.find(p => p.id === parseInt(selectedProject))?.name : t('hoursImport.noProject').replace('-- ', '').replace(' --', '');

    const confirmMessage = t('hoursImport.confirmImport')
      .replace('{count}', lines)
      .replace('{user}', userName)
      .replace('{project}', projectName);

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await api.post('/hours-adjustment/import', {
        user_id: selectedUser || null,
        csv_data: csvData,
        project_id: selectedProject ? parseInt(selectedProject) : null
      });

      setResult(response.data);
      
      if (response.data.error_count === 0) {
        alert(`✅ ${t('hoursImport.importSuccess')}\n\n${response.data.message}`);
        setCsvData(''); // Clear after successful import
      } else {
        alert(`⚠️ ${t('hoursImport.importWithErrors')}\n\n${response.data.message}`);
      }
    } catch (error) {
      console.error('Error in CSV import:', error);
      const errorMsg = error.response?.data?.error || t('messages.saveError');
      alert(`❌ ${errorMsg}`);
      setResult({ 
        success: false, 
        message: errorMsg,
        error_count: 1
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setCsvData(e.target.result);
    };
    reader.readAsText(file);
  };

  return (
    <div className="hours-import-container">
      <div className="hours-import-header">
        <h1>📊 {t('hoursImport.title')}</h1>
        <p className="subtitle">{t('hoursImport.subtitle')}</p>
      </div>

      <div className="import-form">
        <div className="form-section">
          <h3>1️⃣ {t('hoursImport.selectUser')}</h3>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="form-select"
            disabled={loading}
          >
            <option value="">Automaticky podľa mena v import liste</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.username}{u.internal_number ? ` / ${u.internal_number}` : ''}
              </option>
            ))}
          </select>
          <p className="form-hint">Pri import liste s blokmi typu „Meno Priezvisko / číslo“ môžeš používateľa nechať automaticky. Systém najprv hľadá interné číslo používateľa, potom meno. Ak sa nič nenájde, vyber používateľa ručne pre jeden blok.</p>
        </div>

        <div className="form-section">
          <h3>2️⃣ {t('hoursImport.selectProject')}</h3>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="form-select"
            disabled={loading}
          >
            <option value="">{t('hoursImport.noProject')}</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <p className="form-hint">{t('hoursImport.projectHint')}</p>
        </div>

        <div className="form-section">
          <h3>3️⃣ {t('hoursImport.enterOrUpload')}</h3>
          
          <div className="file-upload-section">
            <label htmlFor="file-upload" className="file-upload-label">
              📁 {t('hoursImport.uploadFile')}
            </label>
            <input
              id="file-upload"
              type="file"
              accept=".txt,.csv,.tsv"
              onChange={handleFileUpload}
              className="file-upload-input"
              disabled={loading}
            />
          </div>

          <div className="csv-format-info">
            <h4>📋 {t('hoursImport.csvFormat')}</h4>
            <div className="format-example">
              <code>
                Marek Host / 252 42,50-47,50<br/>
                Week&nbsp;&nbsp;Date&nbsp;&nbsp;Name&nbsp;&nbsp;place&nbsp;&nbsp;hours&nbsp;&nbsp;hours&nbsp;&nbsp;minutes<br/>
                8&nbsp;&nbsp;16.2.2026&nbsp;&nbsp;&nbsp;&nbsp;DAY OFF&nbsp;&nbsp;0,00&nbsp;&nbsp;0&nbsp;&nbsp;0<br/>
                8&nbsp;&nbsp;17.2.2026&nbsp;&nbsp;&nbsp;&nbsp;DAY OFF&nbsp;&nbsp;0,00&nbsp;&nbsp;0&nbsp;&nbsp;0<br/>
                <br/>
                podporované je aj pôvodné CSV: 8,02 8 1 11 10.3.2025
              </code>
            </div>
            <p className="format-description">
              <strong>{t('common.note') || 'Poznámka'}:</strong> Vlož celý import list naraz. Hlavička „Meno / číslo 42,50-47,50“ použije číslo na nájdenie používateľa a uloží fakturačné sadzby od daného týždňa. Riadky s miestom obsahujúcim „night“ sa uložia ako nočná pozícia. Súhrnné riadky Total, working day, weekend, Price together a Monthly sa automaticky preskočia.
            </p>
          </div>

          <textarea
            value={csvData}
            onChange={(e) => setCsvData(e.target.value)}
            placeholder={t('hoursImport.csvPlaceholder')}
            className="csv-textarea"
            disabled={loading}
            rows={12}
          />
        </div>

        <div className="form-actions">
          <button
            onClick={handleImport}
            className="btn-import"
            disabled={loading || !csvData.trim()}
          >
            {loading ? `⏳ ${t('hoursImport.importing')}` : `📥 ${t('hoursImport.importButton')}`}
          </button>

          {csvData && (
            <button
              onClick={() => setCsvData('')}
              className="btn-clear"
              disabled={loading}
            >
              🗑️ {t('hoursImport.clearButton')}
            </button>
          )}
        </div>

        {result && (
          <div className={`import-result ${result.error_count > 0 ? 'warning' : 'success'}`}>
            <h3>{result.error_count === 0 ? `✅ ${t('hoursImport.importSuccess')}` : `⚠️ ${t('hoursImport.importWithErrors')}`}</h3>
            <p className="result-message">{result.message}</p>
            
            <div className="result-stats">
              <div className="stat-item success">
                <span className="stat-label">✨ {t('hoursImport.newRecords')}</span>
                <span className="stat-value">{result.inserted_count || 0}</span>
              </div>
              <div className="stat-item success">
                <span className="stat-label">🔄 {t('hoursImport.updatedRecords')}</span>
                <span className="stat-value">{result.updated_count || 0}</span>
              </div>
              {result.error_count > 0 && (
                <div className="stat-item error">
                  <span className="stat-label">❌ {t('hoursImport.errors')}</span>
                  <span className="stat-value">{result.error_count}</span>
                </div>
              )}
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="error-details">
                <h4>{t('hoursImport.errorDetails')}</h4>
                <ul>
                  {result.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HoursImport;
